// Clinical chief-complaint area definitions used across the New Session workflow.
// Each area maps to:
//   - label: display name shown to therapists
//   - rom: an ordered list of ROM measurement labels relevant to that area
//   - synonyms: optional plain-English alternates the therapist might type
// The first listed ROM measurements appear by default when the area is chosen
// at baseline. Reassessment in Post-Treatment reads the SAME labels back so the
// therapist only re-enters the "after" degrees.

export const CHIEF_COMPLAINT_AREAS = [
  {
    key: "cervical",
    label: "Cervical / Neck",
    rom: [
      "Cervical rotation (L)",
      "Cervical rotation (R)",
      "Cervical flexion",
      "Cervical extension",
      "Cervical lateral flexion (L)",
      "Cervical lateral flexion (R)",
    ],
  },
  {
    key: "thoracic",
    label: "Thoracic / Mid-back",
    rom: [
      "Thoracic rotation (L)",
      "Thoracic rotation (R)",
      "Thoracic extension",
    ],
  },
  {
    key: "lumbar",
    label: "Lumbar / Low Back",
    rom: [
      "Lumbar flexion",
      "Lumbar extension",
      "Lumbar lateral flexion (L)",
      "Lumbar lateral flexion (R)",
    ],
  },
  {
    key: "shoulder",
    label: "Shoulder",
    rom: [
      "Shoulder flexion (L)",
      "Shoulder flexion (R)",
      "Shoulder abduction (L)",
      "Shoulder abduction (R)",
      "Shoulder external rotation (L)",
      "Shoulder external rotation (R)",
    ],
  },
  {
    key: "hip",
    label: "Hip",
    rom: [
      "Hip flexion (L)",
      "Hip flexion (R)",
      "Hip extension (L)",
      "Hip extension (R)",
      "Hip abduction (L)",
      "Hip abduction (R)",
    ],
  },
  {
    key: "knee",
    label: "Knee",
    rom: [
      "Knee flexion (L)",
      "Knee flexion (R)",
      "Knee extension (L)",
      "Knee extension (R)",
    ],
  },
  {
    key: "ankle",
    label: "Ankle / Foot",
    rom: [
      "Ankle dorsiflexion (L)",
      "Ankle dorsiflexion (R)",
      "Ankle plantarflexion (L)",
      "Ankle plantarflexion (R)",
    ],
  },
  {
    key: "wrist",
    label: "Wrist / Hand",
    rom: [
      "Wrist flexion (L)",
      "Wrist flexion (R)",
      "Wrist extension (L)",
      "Wrist extension (R)",
    ],
  },
  {
    key: "elbow",
    label: "Elbow",
    rom: [
      "Elbow flexion (L)",
      "Elbow flexion (R)",
      "Elbow extension (L)",
      "Elbow extension (R)",
    ],
  },
];

export function areaByKey(key) {
  return CHIEF_COMPLAINT_AREAS.find((a) => a.key === key) || null;
}

export function romPresetsForArea(key) {
  return (areaByKey(key)?.rom) || [];
}

// Returns a merged ROM measurement list seeded with the area's preset labels.
// Existing measurements (by label) are preserved and not duplicated.
export function seedRomForArea(key, existing = []) {
  const presets = romPresetsForArea(key);
  if (!presets.length) return existing;
  const labels = new Set(existing.map((m) => m.label));
  const seeded = presets
    .filter((label) => !labels.has(label))
    .map((label) => ({ label, before: null, after: null, target: null }));
  return [...existing, ...seeded];
}

// Compute % improvement helpers — surfaced inline on summary cards.
export function painPctImprovement(before, after) {
  if (before == null || after == null) return null;
  if (Number(before) <= 0) return null;
  return Math.round(((Number(before) - Number(after)) / Number(before)) * 100);
}

export function romPctImprovement(before, after) {
  if (before == null || after == null) return null;
  if (Number(before) <= 0) return null;
  return Math.round(((Number(after) - Number(before)) / Number(before)) * 100);
}
